package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.nutritionist.models.ProductWithNutrition
import com.example.borshi.web.nutritionist.repository.NutritionistRepository
import com.example.borshi.web.nutritionist.repository.ProductItem
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Browse Food Catalog with Nutrition Information
 * Shows products from 'products' collection with nutrition data
 */
@Composable
fun FoodCatalogBrowserView(repository: NutritionistRepository) {
    var products by remember { mutableStateOf<List<ProductItem>>(emptyList()) }
    var searchQuery by remember { mutableStateOf("") }
    var selectedCategory by remember { mutableStateOf("All") }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var selectedProduct by remember { mutableStateOf<ProductItem?>(null) }
    var showDetailModal by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()

    // Load products
    LaunchedEffect(selectedCategory, searchQuery) {
        scope.launch {
            isLoading = true
            val filters = if (selectedCategory != "All") {
                mapOf("category" to selectedCategory)
            } else {
                emptyMap()
            }

            repository.searchProducts(searchQuery, filters)
                .onSuccess {
                    products = it
                    isLoading = false
                    console.log("✅ Loaded ${it.size} products")
                }
                .onFailure {
                    error = it.message
                    isLoading = false
                }
        }
    }

    Div({ style { padding(24.px) } }) {
        H2({
            style {
                fontSize(28.px)
                marginBottom(24.px)
                color(Color("#333"))
            }
        }) {
            Text("🍽️ Food Catalog & Nutrition Database")
        }

        // Search and Filter Bar
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
                marginBottom(24.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            // Search Input
            Div({ style { flex(1); minWidth(300.px) } }) {
                SearchBar(
                    value = searchQuery,
                    placeholder = "Search products...",
                    onValueChange = { searchQuery = it }
                )
            }

            // Category Filter
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                CategoryPill("All", selectedCategory == "All") {
                    selectedCategory = "All"
                }
                CategoryPill("Fish", selectedCategory == "Fish") {
                    selectedCategory = "Fish"
                }
                CategoryPill("Shellfish", selectedCategory == "Shellfish") {
                    selectedCategory = "Shellfish"
                }
                CategoryPill("Prawns", selectedCategory == "Prawns") {
                    selectedCategory = "Prawns"
                }
                CategoryPill("Dried", selectedCategory == "Dried") {
                    selectedCategory = "Dried"
                }
            }
        }

        // Stats Summary
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                gap(12.px)
                marginBottom(24.px)
            }
        }) {
            CatalogStat("📦 Total Products", products.size.toString(), "#2196F3")
            CatalogStat("✅ Available", products.count { it.isAvailable }.toString(), "#4CAF50")
            CatalogStat("💪 High Protein", products.count { it.protein > 20 }.toString(), "#9C27B0")
            CatalogStat("🐟 High Omega-3", products.count { it.omega3 > 1000 }.toString(), "#FF9800")
        }

        // Product Grid
        when {
            isLoading -> LoadingSpinner()
            error != null -> ErrorMessage(error!!)
            products.isEmpty() -> EmptyState(
                icon = "🔍",
                title = "No Products Found",
                message = "Try adjusting your search or filter criteria"
            )
            else -> {
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fill, minmax(280px, 1fr))")
                        gap(20.px)
                    }
                }) {
                    products.forEach { product ->
                        ProductCatalogCard(product) {
                            selectedProduct = product
                        }
                    }
                }
            }
        }

        // Product Detail Modal
        selectedProduct?.let { product ->
            ProductDetailModal(
                product = product,
                onClose = { selectedProduct = null }
            )
        }
    }
}

@Composable
private fun CategoryPill(label: String, isActive: Boolean, onClick: () -> Unit) {
    Div({
        style {
            padding(8.px, 16.px)
            backgroundColor(if (isActive) Color("#2196F3") else Color("#f5f5f5"))
            color(if (isActive) Color.white else Color("#333"))
            borderRadius(20.px)
            cursor("pointer")
            fontSize(14.px)
            fontWeight(if (isActive) "600" else "normal")
            property("transition", "all 0.2s ease")
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun CatalogStat(label: String, value: String, color: String) {
    Div({
        style {
            backgroundColor(Color.white)
            padding(16.px)
            borderRadius(8.px)
            property("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
            textAlign("center")
        }
    }) {
        Div({ style { fontSize(24.px); fontWeight("bold"); color(Color(color)); marginBottom(4.px) } }) {
            Text(value)
        }
        Div({ style { fontSize(12.px); color(Color("#666")) } }) {
            Text(label)
        }
    }
}

@Composable
private fun ProductCatalogCard(product: ProductItem, onClick: () -> Unit) {
    Div({
        style {
            backgroundColor(Color.white)
            borderRadius(12.px)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            cursor("pointer")
            property("transition", "all 0.2s ease")
            overflow("hidden")
        }
        onClick { onClick() }
    }) {
        // Image with proper Firebase Storage URL handling
        Div({
            style {
                width(100.percent)
                height(180.px)
                backgroundColor(Color("#f5f5f5"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                overflow("hidden")
            }
        }) {
            Img(
                src = product.imageUrl,
                alt = product.name,
                attrs = {
                    style {
                        width(100.percent)
                        height(100.percent)
                        property("object-fit", "cover")
                    }
                    attr("onerror", "this.src='" + FirebaseService.getPlaceholderImageUrl() + "'")
                }
            )
        }

        // Content
        Div({ style { padding(16.px) } }) {
            H3({ style { margin(0.px); fontSize(18.px); marginBottom(8.px); color(Color("#333")) } }) {
                Text(product.name)
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(12.px)
                }
            }) {
                Span({ style { fontSize(14.px); color(Color("#999")) } }) {
                    Text(product.category)
                }
                Span({ style { fontSize(18.px); fontWeight("bold"); color(Color("#2196F3")) } }) {
                    Text("৳${product.price.toInt()}")
                }
            }

            // Nutrition Highlights
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "1fr 1fr")
                    gap(8.px)
                    marginBottom(12.px)
                }
            }) {
                NutritionBadge("💪", "${product.protein}g", "Protein")
                NutritionBadge("🔥", "${product.calories.toInt()}", "Cal")
            }

            // Tags
            if (product.tags.isNotEmpty()) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(6.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    product.tags.take(2).forEach { tag ->
                        Span({
                            style {
                                fontSize(11.px)
                                padding(4.px, 8.px)
                                backgroundColor(Color("#E3F2FD"))
                                color(Color("#1976D2"))
                                borderRadius(4.px)
                            }
                        }) {
                            Text(tag)
                        }
                    }
                }
            }

            // Availability
            Div({
                style {
                    marginTop(12.px)
                    fontSize(12.px)
                    color(if (product.isAvailable) Color("#4CAF50") else Color("#F44336"))
                    fontWeight("600")
                }
            }) {
                Text(if (product.isAvailable) "✓ Available" else "✗ Out of Stock")
            }
        }
    }
}

@Composable
private fun NutritionBadge(icon: String, value: String, label: String) {
    Div({
        style {
            backgroundColor(Color("#f5f5f5"))
            padding(8.px)
            borderRadius(6.px)
            textAlign("center")
        }
    }) {
        Div({ style { fontSize(16.px); marginBottom(2.px) } }) {
            Text("$icon $value")
        }
        Div({ style { fontSize(10.px); color(Color("#999")) } }) {
            Text(label)
        }
    }
}

@Composable
private fun ProductDetailModal(product: ProductItem, onClose: () -> Unit) {
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0,0,0,0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(20.px)
        }
        onClick { onClose() }
    }) {
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(12.px)
                maxWidth(900.px)
                width(100.percent)
                maxHeight(90.vh)
                overflowY("auto")
                property("box-shadow", "0 4px 20px rgba(0,0,0,0.3)")
            }
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({
                style {
                    padding(24.px)
                    property("border-bottom", "1px solid #eee")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Start)
                }
            }) {
                Div({ style { flex(1) } }) {
                    H2({ style { margin(0.px); fontSize(28.px); color(Color("#333")); marginBottom(8.px) } }) {
                        Text(product.name)
                    }
                    Div({ style { fontSize(14.px); color(Color("#666")) } }) {
                        Text("${product.category} • Stock: ${product.stockQuantity} ${product.unit}")
                    }
                    Div({ style { fontSize(20.px); fontWeight("bold"); color(Color("#2196F3")); marginTop(8.px) } }) {
                        Text("৳${product.price}")
                    }
                }

                Button({
                    style {
                        backgroundColor(Color("transparent"))
                        border(0.px)
                        fontSize(28.px)
                        cursor("pointer")
                        padding(0.px)
                        width(40.px)
                        height(40.px)
                    }
                    onClick { onClose() }
                }) {
                    Text("×")
                }
            }

            // Product Image
            Div({
                style {
                    padding(24.px)
                    textAlign("center")
                    backgroundColor(Color("#f8f9fa"))
                }
            }) {
                Img(FirebaseService.getProductImageUrl(product.productId)) {
                    style {
                        maxWidth(400.px)
                        width(100.percent)
                        height(250.px)
                        property("object-fit", "cover")
                        borderRadius(12.px)
                    }
                    attr("onerror", "this.src='" + FirebaseService.getPlaceholderImageUrl() + "'")
                }
            }

            // Comprehensive Nutrition Section
            Div({ style { padding(24.px) } }) {
                H3({ style { fontSize(20.px); marginBottom(16.px); color(Color("#333")) } }) {
                    Text("📊 Complete Nutrition Profile (per 100g)")
                }

                // Macronutrients Grid
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fit, minmax(150px, 1fr))")
                        gap(12.px)
                        marginBottom(20.px)
                    }
                }) {
                    DetailedNutritionCard("Protein", "${product.protein}g", "#4CAF50", "Essential for muscle growth")
                    DetailedNutritionCard("Calories", "${product.calories} kcal", "#FF9800", "Energy content")
                    DetailedNutritionCard("Omega-3", "${product.omega3}mg", "#2196F3", "Heart & brain health")
                    DetailedNutritionCard("Fat", "${product.fat}g", "#9C27B0", "Healthy fats")
                    DetailedNutritionCard("Carbs", "${product.carbohydrates}g", "#00BCD4", "Energy source")
                    DetailedNutritionCard("Fiber", "${product.fiber}g", "#8BC34A", "Digestive health")
                }

                // Vitamins & Minerals
                if (product.vitaminD > 0 || product.vitaminB12 > 0 || product.iron > 0) {
                    H4({ style { fontSize(16.px); marginTop(24.px); marginBottom(12.px); color(Color("#666")) } }) {
                        Text("💊 Vitamins & Minerals")
                    }
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "repeat(3, 1fr)")
                            gap(12.px)
                            marginBottom(20.px)
                        }
                    }) {
                        if (product.vitaminD > 0) {
                            MicronutrientBadge("Vitamin D", "${product.vitaminD}IU", "Bone health")
                        }
                        if (product.vitaminB12 > 0) {
                            MicronutrientBadge("Vitamin B12", "${product.vitaminB12}mcg", "Nerve function")
                        }
                        if (product.iron > 0) {
                            MicronutrientBadge("Iron", "${product.iron}mg", "Blood health")
                        }
                    }
                }

                // Health Benefits
                if (product.tags.isNotEmpty()) {
                    H4({ style { fontSize(16.px); marginTop(24.px); marginBottom(12.px); color(Color("#666")) } }) {
                        Text("💚 Health Benefits & Features")
                    }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(8.px)
                            flexWrap(FlexWrap.Wrap)
                            marginBottom(20.px)
                        }
                    }) {
                        product.tags.forEach { tag ->
                            Span({
                                style {
                                    fontSize(13.px)
                                    padding(8.px, 16.px)
                                    backgroundColor(Color("#E8F5E9"))
                                    color(Color("#2E7D32"))
                                    borderRadius(20.px)
                                    fontWeight("500")
                                }
                            }) {
                                Text("✓ $tag")
                            }
                        }
                    }
                }

                // Recommended Usage
                Div({
                    style {
                        backgroundColor(Color("#E3F2FD"))
                        padding(16.px)
                        borderRadius(8.px)
                        marginTop(20.px)
                    }
                }) {
                    H4({ style { fontSize(14.px); margin(0.px); marginBottom(8.px); color(Color("#1976D2")) } }) {
                        Text("🍴 Recommended Usage")
                    }
                    P({ style { margin(0.px); fontSize(13.px); color(Color("#666")); lineHeight("1.6") } }) {
                        Text(getUsageRecommendation(product))
                    }
                }

                // Availability Status
                Div({
                    style {
                        marginTop(20.px)
                        padding(16.px)
                        backgroundColor(if (product.isAvailable) Color("#E8F5E9") else Color("#FFEBEE"))
                        borderRadius(8.px)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Div {
                        Div({ style { fontSize(14.px); fontWeight("600"); color(if (product.isAvailable) Color("#2E7D32") else Color("#C62828")) } }) {
                            Text(if (product.isAvailable) "✓ In Stock" else "✗ Out of Stock")
                        }
                        Div({ style { fontSize(12.px); color(Color("#666")); marginTop(4.px) } }) {
                            Text("${product.stockQuantity} ${product.unit} available")
                        }
                    }
                    if (product.isAvailable) {
                        Button({
                            style {
                                padding(10.px, 20.px)
                                backgroundColor(Color("#4CAF50"))
                                color(Color.white)
                                border(0.px)
                                borderRadius(6.px)
                                cursor("pointer")
                                fontSize(14.px)
                                fontWeight("600")
                            }
                            onClick { console.log("Add to plan: ${product.productId}") }
                        }) {
                            Text("Add to Plan")
                        }
                    }
                }
            }
        }
    }
}

// Helper Components
@Composable
private fun DetailedNutritionCard(label: String, value: String, color: String, description: String) {
    Div({
        style {
            backgroundColor(Color.white)
            padding(16.px)
            borderRadius(8.px)
            border(1.px, LineStyle.Solid, Color("#eee"))
            textAlign("center")
        }
    }) {
        Div({ style { fontSize(24.px); fontWeight("bold"); color(Color(color)); marginBottom(4.px) } }) {
            Text(value)
        }
        Div({ style { fontSize(14.px); fontWeight("600"); color(Color("#333")); marginBottom(4.px) } }) {
            Text(label)
        }
        Div({ style { fontSize(11.px); color(Color("#999")) } }) {
            Text(description)
        }
    }
}

@Composable
private fun MicronutrientBadge(name: String, value: String, benefit: String) {
    Div({
        style {
            backgroundColor(Color("#FFF3E0"))
            padding(12.px)
            borderRadius(6.px)
        }
    }) {
        Div({ style { fontSize(13.px); fontWeight("600"); color(Color("#333")); marginBottom(2.px) } }) {
            Text("$name: $value")
        }
        Div({ style { fontSize(11.px); color(Color("#666")) } }) {
            Text(benefit)
        }
    }
}

private fun getUsageRecommendation(product: ProductItem): String {
    return when {
        product.protein > 20 -> "Excellent source of high-quality protein. Ideal for muscle building and post-workout meals. Recommended serving: 150-200g per meal."
        product.omega3 > 1000 -> "Rich in Omega-3 fatty acids. Great for heart health and brain function. Consume 2-3 times per week for optimal benefits."
        product.calories < 150 -> "Low in calories, perfect for weight management. Can be consumed frequently as part of a balanced diet."
        else -> "Versatile seafood option. Can be grilled, steamed, or baked. Pairs well with vegetables and whole grains."
    }
}

